// src/pages/documents/EntityRowSelector.tsx

import React from 'react';
import { Input, Select, Space, Typography } from 'antd';
import { MappableEntity } from '../../types';

const { Text } = Typography;
const { Option } = Select;

/**
 * Picks which row a mapping means, for entities an estate holds several of.
 *
 * An estate has many assets, many participants, many liabilities, so naming the
 * entity and the field does not say what fills the box: "asset market value" is
 * not an answer while there are four assets. This control supplies the missing
 * half of the answer.
 *
 * Rows are picked by position or by a matching value, never by id: a document is
 * filled against whichever estate is chosen at download time, so an id would
 * only ever match one estate. The two forms the backend understands:
 *
 *   "[1]"                                zero-based position
 *   "[relationshipToDecedent=Spouse]"    first row whose field matches
 *
 * An empty value means the first row, which is how mappings behaved before a
 * row could be chosen.
 */

export interface EntityRowSelectorProps {
  entity: MappableEntity;
  value: string;
  onChange: (selector: string) => void;
  /**
   * Changes when the control is pointed at a different mapping, so the choice
   * between position and matching is re-read from that mapping's selector.
   * Without it, editing one field and then another would carry the first
   * field's mode across.
   */
  resetKey?: string | number;
  /** Hides the explanatory line where space is tight, as in a table row. */
  compact?: boolean;
}

const parseMatch = (selector: string): { field: string; value: string } => {
  const equals = selector.indexOf('=');
  if (equals <= 0) return { field: '', value: '' };

  return {
    field: selector.replace(/^\[/, '').slice(0, equals - 1),
    value: selector.replace(/\]$/, '').slice(equals + 1),
  };
};

const EntityRowSelector: React.FC<EntityRowSelectorProps> = ({
  entity,
  value,
  onChange,
  resetKey,
  compact = false,
}) => {
  const canMatch = !!entity.selectBy?.length;
  const [mode, setMode] = React.useState<'position' | 'match'>(
    value.includes('=') ? 'match' : 'position',
  );
  const match = parseMatch(value);

  // Deliberately not derived from the value on every render: choosing "by a
  // matching value" clears the selector until a field and a value are picked,
  // and re-deriving would snap the control back to position mid-edit.
  const latestValue = React.useRef(value);
  latestValue.current = value;
  React.useEffect(() => {
    setMode(latestValue.current.includes('=') ? 'match' : 'position');
  }, [resetKey]);

  const setMatch = (field: string, matchValue: string) => {
    onChange(field && matchValue ? `[${field}=${matchValue}]` : '');
  };

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {canMatch && (
        <Select
          value={mode}
          onChange={(next: 'position' | 'match') => {
            setMode(next);
            onChange('');
          }}
          style={{ width: '100%' }}
        >
          <Option value="position">By position</Option>
          <Option value="match">By a matching value</Option>
        </Select>
      )}

      {mode === 'position' || !canMatch ? (
        <Select
          value={value || '[0]'}
          onChange={onChange}
          style={{ width: '100%' }}
        >
          {['1st', '2nd', '3rd', '4th', '5th'].map((label, index) => (
            <Option key={index} value={`[${index}]`}>
              {`${label} ${entity.label}`}
            </Option>
          ))}
        </Select>
      ) : (
        <Space.Compact style={{ width: '100%' }}>
          <Select
            value={match.field || undefined}
            onChange={(field: string) => setMatch(field, match.value)}
            placeholder="Field..."
            style={{ width: '50%' }}
          >
            {entity.selectBy?.map((f) => (
              <Option key={f.value} value={f.value}>
                {f.label}
              </Option>
            ))}
          </Select>
          <Input
            value={match.value}
            onChange={(e) => setMatch(match.field, e.target.value)}
            placeholder="equals..."
            style={{ width: '50%' }}
          />
        </Space.Compact>
      )}

      {!compact && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          An estate holds several. Leave it on the 1st to fill from whichever
          comes first.
        </Text>
      )}
    </Space>
  );
};

export default EntityRowSelector;
